/**
 * 客户端登录密码加密（浏览器安全）。
 *
 * 从 `/api/login-pubkey` 拉取服务端 RSA 公钥（SPKI base64），用 Web Crypto
 * RSA-OAEP + SHA-256 加密密码后再提交，避免密码以明文出现在请求体中（可被
 * 浏览器开发者工具 / 抓包读取）。与服务端 node:crypto RSA_PKCS1_OAEP_PADDING
 * + oaepHash:'sha256' 字节兼容。
 *
 * 未配置密钥（公钥端点返回 null）时优雅降级为明文，保持本地开发零改动。
 */

const ENC_PREFIX = "enc:";

let cachedKey: CryptoKey | null | undefined; // undefined=未拉取, null=不可用

function base64ToBytes(b64: string): ArrayBuffer {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.buffer;
}

function bytesToBase64(bytes: ArrayBuffer): string {
    const arr = new Uint8Array(bytes);
    let bin = "";
    for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
    return btoa(bin);
}

async function fetchPublicKey(): Promise<CryptoKey | null> {
    if (cachedKey !== undefined) return cachedKey;
    try {
        if (typeof window === "undefined" || !window.crypto?.subtle) {
            cachedKey = null;
            return null;
        }
        const res = await fetch("/api/login-pubkey", { cache: "no-store" });
        if (!res.ok) {
            cachedKey = null;
            return null;
        }
        const data = (await res.json()) as { publicKey?: string | null };
        if (!data.publicKey) {
            cachedKey = null;
            return null;
        }
        const der = base64ToBytes(data.publicKey);
        cachedKey = await window.crypto.subtle.importKey(
            "spki",
            der,
            { name: "RSA-OAEP", hash: "SHA-256" },
            false,
            ["encrypt"]
        );
        return cachedKey;
    } catch {
        cachedKey = null;
        return null;
    }
}

/**
 * 加密密码。成功返回 `enc:<base64>`；公钥不可用时原样返回明文（降级）。
 */
export async function encryptPassword(plain: string): Promise<string> {
    const key = await fetchPublicKey();
    if (!key) return plain;
    try {
        const cipher = await window.crypto.subtle.encrypt(
            { name: "RSA-OAEP" },
            key,
            new TextEncoder().encode(plain).buffer as ArrayBuffer
        );
        return ENC_PREFIX + bytesToBase64(cipher);
    } catch {
        return plain;
    }
}
